import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import {
  getCycleCount, countItem, completeCycleCount, lookupBarcode,
} from '../lib/api';
import BarcodeScanner from '../components/BarcodeScanner';
import UomPicker from '../components/UomPicker';
import { Button } from '../components/ui/button';
import {
  X, Barcode, Check, ArrowLeft, Minus, Plus, CaretRight, MagnifyingGlass, Warning,
} from '@phosphor-icons/react';

/**
 * Mobile-first full-screen cycle-count data entry.
 * Route: /cycle-counts/:id/count
 *
 * Designed for warehouse tablets: big fingers, minimal chrome, scanner-first.
 * - Scan a location or SKU barcode → auto-selects the matching target row
 * - Manual "+/-" steppers + big number input
 * - Auto-advances to next pending target after save
 * - Role-open to pickers (not just managers) so floor staff can use it
 */
export default function CountMode() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);          // current target being counted
  const [qty, setQty] = useState('');
  const [saving, setSaving] = useState(false);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [scanHint, setScanHint] = useState('');        // last scan feedback
  // iter164 — UOM picker state. Defaults to active row's base UOM on each
  // new active target; operator can override per count.
  const [countUom, setCountUom] = useState('');

  const refresh = async () => {
    try {
      const { data } = await getCycleCount(id);
      setDetail(data);
      // If nothing active, default to first pending target
      if (!active && data.pending_targets?.length) {
        setActive(data.pending_targets[0]);
      }
      // If the active target is no longer pending (already counted), advance
      if (active && data.pending_targets?.length) {
        const stillPending = data.pending_targets.find(
          t => t.location_id === active.location_id && t.variant_id === active.variant_id
        );
        if (!stillPending) setActive(data.pending_targets[0]);
      }
      if (active && !data.pending_targets?.length) {
        setActive(null);
      }
    } catch (e) {
      toast.error('Failed to load cycle count');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [id]);

  const counted = detail?.counted_items?.length || 0;
  const remaining = detail?.pending_targets?.length || 0;
  const total = counted + remaining;
  const progressPct = total > 0 ? Math.round((counted / total) * 100) : 0;

  const handleScan = async (decoded) => {
    setScannerOpen(false);
    if (!decoded) return;
    setScanHint(`Scanned: ${decoded}`);
    try {
      const { data } = await lookupBarcode(decoded);
      if (data.type === 'location') {
        const locId = data.data.id || data.data._id;
        const match = detail.pending_targets?.find(t => t.location_id === locId);
        if (match) {
          setActive(match);
          setQty('');
          toast.success(`📍 Jumped to ${match.location_code} · ${match.sku_code}`);
        } else {
          toast(`Location ${data.data.location_code} has no remaining targets`);
        }
      } else if (data.type === 'sku' || data.type === 'variant') {
        const skuId = data.type === 'sku' ? (data.data.id || data.data._id) : data.data.sku_id;
        // Find pending targets for this SKU
        const matches = detail.pending_targets?.filter(t =>
          (data.type === 'sku' && t.sku_code === data.data.sku_code) ||
          (data.type === 'variant' && t.variant_id === (data.data.id || data.data._id))
        ) || [];
        if (matches.length === 1) {
          setActive(matches[0]);
          setQty('');
          toast.success(`📦 ${matches[0].sku_code} · ${matches[0].location_code}`);
        } else if (matches.length > 1) {
          setActive(matches[0]);
          setQty('');
          toast(`${matches.length} locations left for ${data.data.sku_code || data.data.name}`);
        } else {
          toast(`SKU ${data.data.sku_code || ''} has no remaining targets`);
        }
      } else {
        toast(`Scanned ${data.type} — not a location or SKU`);
      }
    } catch (e) {
      toast.error('Barcode not recognised');
    }
  };

  const submit = async () => {
    if (!active) return;
    const n = parseFloat(qty);
    if (Number.isNaN(n) || n < 0) {
      toast.error('Enter a valid non-negative count');
      return;
    }
    setSaving(true);
    try {
      const body = {
        location_id: active.location_id,
        variant_id: active.variant_id,
        counted_quantity: n,
      };
      // iter164 — attach uom only when operator picked a non-base UOM
      const baseUom = (active.unit_of_measure || 'each').toLowerCase();
      if (countUom && countUom !== baseUom) body.uom = countUom;
      const { data } = await countItem(id, body);
      if (data.variance === 0) {
        toast.success(`✓ Match · ${n}`);
      } else if (data.variance > 0) {
        toast(`+${data.variance} over expected`, { icon: '📈' });
      } else {
        toast(`${data.variance} short of expected`, { icon: '📉' });
      }
      setQty('');
      setCountUom('');
      setActive(null); // refresh will pick next pending
      await refresh();
    } catch (e) {
      toast.error('Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleComplete = async () => {
    setSaving(true);
    try {
      const { data } = await completeCycleCount(id);
      toast.success(data.message);
      navigate('/cycle-counts');
    } catch (e) {
      toast.error('Complete failed');
    } finally {
      setSaving(false);
    }
  };

  const isDone = !loading && remaining === 0;

  return (
    <div className="fixed inset-0 bg-slate-950 text-white flex flex-col overflow-hidden" data-testid="count-mode-screen">
      {/* Header bar */}
      <div className="flex items-center justify-between px-4 py-3 bg-slate-900 border-b border-slate-800">
        <Link
          to="/cycle-counts"
          data-testid="count-mode-back"
          className="flex items-center gap-2 text-slate-300 hover:text-white text-sm font-bold uppercase tracking-widest"
        >
          <ArrowLeft size={18} weight="bold" /> Exit
        </Link>
        <div className="text-center">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Count</div>
          <div className="font-mono font-bold text-sm">{detail?.count_number || '…'}</div>
        </div>
        <button
          onClick={() => setScannerOpen(true)}
          data-testid="count-mode-scan-btn"
          className="bg-brand hover:bg-[#003dd1] px-4 py-2 font-bold text-xs uppercase tracking-widest flex items-center gap-2"
        >
          <Barcode size={18} weight="fill" /> Scan
        </button>
      </div>

      {/* Progress strip */}
      <div className="px-4 py-2 bg-slate-900 border-b border-slate-800">
        <div className="flex items-center justify-between text-xs font-mono mb-1">
          <span className="text-muted-foreground">
            {counted} / {total} counted · {remaining} remaining
          </span>
          <span className="font-bold text-[#66CC8A]">{progressPct}%</span>
        </div>
        <div className="h-1 bg-slate-800 overflow-hidden">
          <div
            className="h-full bg-[#66CC8A] transition-all"
            style={{ width: `${progressPct}%` }}
            data-testid="count-mode-progress"
          />
        </div>
        {scanHint && (
          <div className="text-[10px] font-mono text-muted-foreground mt-1 truncate">{scanHint}</div>
        )}
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto p-4">
        {loading ? (
          <div className="text-center py-20 text-muted-foreground">Loading…</div>
        ) : isDone ? (
          <div className="text-center py-16" data-testid="count-mode-done">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-[#66CC8A]/20 border-2 border-[#66CC8A] rounded-full mb-4">
              <Check size={40} weight="bold" className="text-[#66CC8A]" />
            </div>
            <h2 className="text-3xl font-bold tracking-tight mb-2">
              All targets counted
            </h2>
            <p className="text-muted-foreground mb-6 text-sm">
              {counted} item(s) counted. Ready to complete?
            </p>
            <Button
              onClick={handleComplete}
              disabled={saving || counted === 0}
              data-testid="count-mode-complete-btn"
              className="bg-[#66CC8A] hover:bg-[#4fb374] text-slate-950 font-bold px-8 py-6 h-auto text-base uppercase tracking-widest rounded-none"
            >
              Finish Count
            </Button>
          </div>
        ) : active ? (
          <div className="space-y-5">
            {/* Active row */}
            <div className="bg-slate-900 border-2 border-brand p-5" data-testid="count-mode-active">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-[#66CC8A] font-bold mb-2">
                <MagnifyingGlass size={12} weight="bold" /> Now counting
              </div>
              <div className="text-4xl font-bold font-mono mb-1" data-testid="count-mode-active-location">
                {active.location_code || active.location_id?.slice(-8)}
              </div>
              <div className="text-muted-foreground text-sm mb-4">
                Zone {active.zone || '—'} · SKU <span className="text-white font-mono font-bold" data-testid="count-mode-active-sku">{active.sku_code}</span>
              </div>

              {/* Qty stepper */}
              <div className="flex items-center gap-3 mb-4">
                <button
                  onClick={() => setQty(q => String(Math.max(0, (parseInt(q || '0', 10) || 0) - 1)))}
                  data-testid="count-mode-dec"
                  className="w-16 h-16 bg-slate-800 hover:bg-slate-700 flex items-center justify-center font-bold text-2xl border border-slate-700"
                >
                  <Minus size={28} weight="bold" />
                </button>
                <input
                  type="number"
                  inputMode={active.allow_fractional ? 'decimal' : 'numeric'}
                  min="0"
                  step={active.allow_fractional ? '0.01' : '1'}
                  value={qty}
                  onChange={(e) => setQty(active.allow_fractional ? e.target.value : e.target.value.replace(/[^0-9]/g, ''))}
                  placeholder="0"
                  data-testid="count-mode-qty-input"
                  className="flex-1 h-16 bg-slate-950 border-2 border-slate-700 focus:border-[#66CC8A] outline-none text-center font-mono font-bold text-4xl text-white"
                />
                <button
                  onClick={() => setQty(q => String((parseInt(q || '0', 10) || 0) + 1))}
                  data-testid="count-mode-inc"
                  className="w-16 h-16 bg-slate-800 hover:bg-slate-700 flex items-center justify-center font-bold text-2xl border border-slate-700"
                >
                  <Plus size={28} weight="bold" />
                </button>
              </div>

              {/* iter164 — UOM picker (only renders when SKU has alternates) */}
              {(active.uom_conversions && active.uom_conversions.length > 0) && (
                <div className="mb-4 flex items-center gap-2 text-xs">
                  <span className="text-muted-foreground uppercase tracking-widest font-bold text-[10px]">Counting in</span>
                  <UomPicker
                    sku={{
                      unit_of_measure: active.unit_of_measure,
                      uom_conversions: active.uom_conversions,
                    }}
                    value={countUom || (active.unit_of_measure || '').toLowerCase()}
                    onChange={(code) => setCountUom(code)}
                    testId="count-mode-uom"
                    className="bg-slate-800 border-slate-700 text-white"
                  />
                </div>
              )}

              <Button
                onClick={submit}
                disabled={saving || qty === ''}
                data-testid="count-mode-save"
                className="w-full bg-[#66CC8A] hover:bg-[#4fb374] text-slate-950 font-bold py-6 h-auto text-base uppercase tracking-widest rounded-none"
              >
                Save & Next
                <CaretRight size={18} weight="bold" className="ml-2" />
              </Button>
            </div>

            {/* Remaining queue (next 5) */}
            {remaining > 1 && (
              <div>
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold mb-2 px-1">
                  Next up ({remaining - 1})
                </div>
                <div className="space-y-1">
                  {(detail.pending_targets || [])
                    .filter(t => !(t.location_id === active.location_id && t.variant_id === active.variant_id))
                    .slice(0, 5)
                    .map(t => (
                      <button
                        key={`${t.location_id}:${t.variant_id}`}
                        onClick={() => { setActive(t); setQty(''); }}
                        data-testid={`count-mode-queue-${t.sku_code}`}
                        className="w-full bg-slate-900 border border-slate-800 hover:border-slate-600 p-3 flex items-center justify-between text-left"
                      >
                        <div>
                          <div className="font-mono font-bold text-sm">{t.location_code}</div>
                          <div className="text-xs text-muted-foreground">{t.sku_code}</div>
                        </div>
                        <CaretRight size={16} weight="bold" className="text-muted-foreground" />
                      </button>
                    ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-20 text-muted-foreground">
            <Warning size={40} weight="fill" className="mx-auto mb-3 text-amber-400" />
            <p>No active target. Scan a location or SKU to start.</p>
          </div>
        )}

        {/* Counted so far (last 3 rolling) */}
        {counted > 0 && (
          <div className="mt-6">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold mb-2 px-1">
              Just counted
            </div>
            <div className="space-y-1">
              {(detail.counted_items || []).slice(-3).reverse().map((it, i) => (
                <div
                  key={i}
                  className={`p-3 border flex items-center justify-between text-sm ${
                    it.variance !== 0
                      ? 'bg-red-950/40 border-red-800'
                      : 'bg-slate-900 border-slate-800'
                  }`}
                >
                  <div>
                    <div className="font-mono font-bold">{it.location_code}</div>
                    <div className="text-xs text-muted-foreground">{it.sku_code}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-mono font-bold">{it.counted_quantity}</div>
                    <div className={`text-xs font-mono ${it.variance > 0 ? 'text-green-400' : it.variance < 0 ? 'text-red-400' : 'text-muted-foreground'}`}>
                      {it.variance === 0 ? '✓ match' : `${it.variance > 0 ? '+' : ''}${it.variance}`}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Bottom action bar */}
      {!isDone && counted > 0 && (
        <div className="p-3 bg-slate-900 border-t border-slate-800 flex items-center gap-2">
          <Button
            variant="outline"
            onClick={handleComplete}
            disabled={saving}
            data-testid="count-mode-finish-early"
            className="flex-1 bg-transparent border-slate-700 text-slate-300 hover:bg-slate-800 hover:text-white rounded-none h-12 font-bold uppercase tracking-widest text-xs"
          >
            Finish Early ({remaining} left)
          </Button>
        </div>
      )}

      {scannerOpen && (
        <BarcodeScanner
          onScan={handleScan}
          onClose={() => setScannerOpen(false)}
          title="Scan location or SKU"
        />
      )}
    </div>
  );
}
